import { useEffect, useState } from "react";
import { formatChronoClock } from "../../utils/coachChronoPlayers";
import "./OffTableChronoCameraHud.css";

function liveSeconds(player, now) {
  if (!player?.running || !player.startedAtWall) {
    return Number(player?.displaySec) || 0;
  }
  const start = Date.parse(player.startedAtWall);
  if (!Number.isFinite(start)) return Number(player.displaySec) || 0;
  return Math.max(0, ((player.baseMs || 0) + (now - start)) / 1000);
}

/**
 * Roster and live chrono clocks for every camera on the station.
 * Times come from the coach off-table chrono sync.
 */
export function OffTableChronoCameraHud({
  socket = null,
  teId = null,
  tsgId = null,
  active = true,
  className = "",
  highlightUserId = null,
  highlightPlayerId = null,
}) {
  const [players, setPlayers] = useState([]);
  const [videoRecording, setVideoRecording] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!socket || !teId || !active) {
      setPlayers([]);
      setVideoRecording(false);
      return undefined;
    }
    const onSync = (payload) => {
      if (!payload || String(payload.te_id) !== String(teId)) return;
      if (
        tsgId &&
        payload.tsg_id &&
        String(payload.tsg_id) !== String(tsgId)
      ) {
        return;
      }
      setPlayers(Array.isArray(payload.players) ? payload.players : []);
      setVideoRecording(Boolean(payload.videoRecording));
    };
    socket.on("offTableChronoSync", onSync);
    return () => socket.off("offTableChronoSync", onSync);
  }, [socket, teId, tsgId, active]);

  const anyRunning = players.some((player) => player.running);
  useEffect(() => {
    if (!anyRunning) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(id);
  }, [anyRunning]);

  if (!active || !players.length) return null;

  return (
    <ol
      className={`lt-chrono-cam${className ? ` ${className}` : ""}${
        videoRecording ? " lt-chrono-cam--live" : ""
      }`}
      aria-label="Training exercise players"
    >
      {players.map((player) => {
        const sec = liveSeconds(player, now);
        const showClock = player.running || sec > 0 || player.savedSec != null;
        const isRecording = Boolean(player.recording);
        const isMine =
          (highlightUserId != null &&
            player.userId != null &&
            String(player.userId) === String(highlightUserId)) ||
          (highlightPlayerId != null &&
            player.playerId != null &&
            String(player.playerId) === String(highlightPlayerId));
        return (
          <li
            key={player.key || player.userId || player.name}
            className={`lt-chrono-cam__row${
              player.running ? " lt-chrono-cam__row--running" : ""
            }${isRecording ? " lt-chrono-cam__row--rec" : ""}${
              isMine ? " lt-chrono-cam__row--mine" : ""
            }`}
          >
            <span className="lt-chrono-cam__name">
              {player.name}
              {isRecording ? (
                <span className="lt-chrono-cam__rec" aria-label="Recording">
                  REC
                </span>
              ) : null}
            </span>
            {showClock ? (
              <span className="lt-chrono-cam__clock">
                {formatChronoClock(
                  player.running
                    ? sec
                    : player.savedSec != null
                      ? Number(player.savedSec)
                      : sec
                )}
              </span>
            ) : (
              <span className="lt-chrono-cam__idle">—</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
